import { useState } from "react";

import { SearchMoviesRequest } from "../../../common/api/catalogue-api/movie/SearchMoviesRequest";
import {
    SearchMoviesResponse,
    SearchMoviesResponseItem
} from "../../../common/api/catalogue-api/movie/SearchMoviesResponse";
import { useCatalogueMovieApi } from "../../../common/api/catalogue-api/movie/useCatalogueMovieApi";
import { SearchAssetsContentAggregationsResponse } from "../../../common/api/master-api/assets/search/content-requests-responses/SearchAssetsContentAggregationsResponse";
import { useAssetsApi } from "../../../common/api/master-api/assets/useAssetsApi";
import { IdAggregation } from "../../../common/types/IdAggregation";
import { MovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { mapToMovieSearchItem, MovieSearchItem } from "./MovieSearchItem";

type MovieSearchArgs = {
    query: string;
    movieContentType: MovieContentSubtype;
    types: number[];
};
type MovieSearchLastModifiedArgs = Omit<MovieSearchArgs, "query">;

export const useMovieSearchListPage = () => {
    const { searchMovies } = useCatalogueMovieApi();
    const { searchAssetsContentAggregations } = useAssetsApi();

    const [movies, setMovies] = useState<MovieSearchItem[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [loadMoreParameters, setLoadMoreParameters] = useState<SearchMoviesRequest>();
    const [cachedAggregations, setCachedAggregations] = useState<SearchAssetsContentAggregationsResponse>({
        aggregations: []
    });
    const [isMoreLoading, setMoreIsLoading] = useState<boolean>(false);

    const pageSize = 50;

    const search = async ({ query, movieContentType, types }: MovieSearchArgs) => {
        if (query) {
            searchByQuery({ query, movieContentType, types });
        } else {
            searchLastModified({ movieContentType, types });
        }
    };

    const searchLastModified = async ({ movieContentType, types }: MovieSearchLastModifiedArgs) => {
        setIsLoading(true);

        const subtype = movieContentType;

        const aggregationsResponse = await searchAssetsContentAggregations({ subtype, types });

        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setListEmpty();
            setIsLoading(false);
            return;
        }

        const movieIds = aggregationsResponse.aggregations.slice(0, pageSize).map((agg) => agg.id);

        searchMovieByIds({ movieIds, subtype })
            .then((response: SearchMoviesResponse) => {
                setTotalCount(response.totalCount);

                const movieItems = mapAndSortResponseToLastModifiedMovieItems(
                    response,
                    aggregationsResponse.aggregations
                );

                setMovies(movieItems);

                setLoadMoreParameters({ query: "", page: 2, pageSize, subtype, movieIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const searchByQuery = async ({ query, movieContentType, types }: MovieSearchArgs) => {
        setIsLoading(true);

        const subtype = movieContentType;

        const aggregationsResponse = await searchAssetsContentAggregations({ query, subtype, types });

        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setListEmpty();
            setIsLoading(false);
            return;
        }

        const movieIds = aggregationsResponse.aggregations.map((agg) => agg.id);

        searchMovies({ query: query, page: 1, pageSize, subtype, movieIds })
            .then((response: SearchMoviesResponse) => {
                setTotalCount(response.totalCount);

                const movieItems = mapResponseToMovieItems(response, aggregationsResponse.aggregations);

                setMovies(movieItems);

                setLoadMoreParameters({ query, page: 2, pageSize, subtype, movieIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const loadMore = async () => {
        if (!loadMoreParameters) {
            throw new Error("Parameters for 'load more' must not be empty.");
        }

        if (loadMoreParameters.query) {
            loadMoreByQuery(loadMoreParameters);
        } else {
            loadMoreLastModified(loadMoreParameters);
        }
    };

    const loadMoreByQuery = async (searchParameters: SearchMoviesRequest) => {
        setMoreIsLoading(true);

        searchMovies({ ...searchParameters })
            .then((response: SearchMoviesResponse) => {
                setTotalCount(response.totalCount);

                const movieItems = mapResponseToMovieItems(response, cachedAggregations.aggregations);

                setMovies([...movies, ...movieItems]);

                setLoadMoreParameters({
                    ...searchParameters,
                    page: searchParameters.page + 1
                });
            })
            .finally(() => {
                setMoreIsLoading(false);
            });
    };

    const loadMoreLastModified = async (searchParameters: SearchMoviesRequest) => {
        setMoreIsLoading(true);

        const movieIds = cachedAggregations.aggregations
            .slice((searchParameters.page - 1) * pageSize, searchParameters.page * pageSize)
            .map((agg) => agg.id);

        searchMovieByIds({ movieIds, subtype: searchParameters.subtype })
            .then((response: SearchMoviesResponse) => {
                setTotalCount(cachedAggregations.aggregations.length);

                const movieItems = mapAndSortResponseToLastModifiedMovieItems(
                    response,
                    cachedAggregations.aggregations
                );

                setMovies([...movies, ...movieItems]);

                setLoadMoreParameters({
                    ...searchParameters,
                    page: searchParameters.page + 1
                });
            })
            .finally(() => {
                setMoreIsLoading(false);
            });
    };

    const searchMovieByIds = async ({
        movieIds,
        subtype
    }: {
        movieIds: string[];
        subtype: MovieContentSubtype;
    }): Promise<SearchMoviesResponse> => {
        return searchMovies({ query: "", pageSize, page: 1, movieIds: movieIds, subtype });
    };

    const setListEmpty = () => {
        setMovies([]);
        setTotalCount(0);
    };

    return {
        movies,
        totalCount,
        isLoading,
        isMoreLoading,
        pageSize,
        search,
        loadMore
    };
};

const mapResponseToMovieItems = (response: SearchMoviesResponse, aggregations: IdAggregation[]) => {
    return response.items.map((item: SearchMoviesResponseItem) => {
        const agg = aggregations.find((aggregation) => aggregation.id === item.id);

        if (!agg) throw new Error("Aggregateion should not be undefined");

        return mapToMovieSearchItem(item, { assetsCount: agg.count, types: agg.types });
    });
};

const mapAndSortResponseToLastModifiedMovieItems = (response: SearchMoviesResponse, aggregations: IdAggregation[]) => {
    const movieSearchItems: MovieSearchItem[] = [];

    aggregations.forEach((agg) => {
        const searchItem = response.items.find((item) => item.id === agg.id);
        if (searchItem) {
            movieSearchItems.push(mapToMovieSearchItem(searchItem, { assetsCount: agg.count, types: agg.types }));
        }
    });

    return movieSearchItems;
};
